import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountPageHeader } from '../../account/components/AccountPageHeader';
import { OrderCard } from '../components/OrderCard';
import { Button, EmptyState, Skeleton } from '@shopmuchmore/design-system';
import { useOrdersListQuery } from '../orders.services';
import { Package, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

export function AccountOrdersPage() {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const { data, isLoading, error, isFetching } = useOrdersListQuery({ page });

    const orders = data?.results || [];
    const totalPages = data?.total_pages || 1;

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        const scrollContainers = document.querySelectorAll('.overflow-auto, .overflow-y-auto');
        scrollContainers.forEach(container => {
            container.scrollTop = 0;
        });
    }, [page]);

    return (
        <div className="w-full space-y-8 pb-12">
            <AccountPageHeader
                title="My Orders"
                description="Track and manage your recent purchases."
            />

            {isLoading ? (
                <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-48 w-full rounded-3xl" />
                    ))}
                </div>
            ) : error ? (
                <EmptyState
                    icon={AlertCircle}
                    title="Failed to load orders"
                    description="There was an error fetching your order history. Please try again."
                    iconClassName="bg-red-50 text-red-500"
                    action={
                        <Button 
                            variant="secondary" 
                            className="rounded-full px-8"
                            onClick={() => window.location.reload()}
                        >
                            Try Again
                        </Button>
                    }
                />
            ) : orders.length > 0 ? (
                <div className="space-y-6">
                    <div className="space-y-4">
                        {orders.map((order) => (
                            <OrderCard key={order.order_number} order={order} />
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center space-x-6 pt-8">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1 || isFetching}
                                className="rounded-full w-10 h-10 p-0 border-slate-200"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>

                            <div className="text-sm font-black text-slate-900 uppercase tracking-widest">
                                Page <span className="text-brand-primary">{page}</span> of {totalPages}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages || isFetching}
                                className="rounded-full w-10 h-10 p-0 border-slate-200"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <EmptyState
                    icon={Package}
                    title="No orders yet"
                    description="Looks like you haven't placed any orders yet. Start exploring our products!"
                    iconClassName="bg-primary/5"
                    action={
                        <Button 
                            className="rounded-full px-8"
                            onClick={() => navigate('/')}
                        >
                            Start Shopping
                        </Button>
                    }
                />
            )}
        </div>
    );
}

